import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X, Megaphone } from "lucide-react";

/**
 * Public ad banner — reads /api/ads/active?slot=... and rotates one of the
 * paid banners for the given slot. Dismissible per session so a visitor
 * isn't nagged on every page.
 *
 * Props:
 *   • slot: "landing_hero" | "marketplace_top" | "services_hero"
 */
export default function AdBanner({ slot, className = "" }) {
    const [ad, setAd] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        try {
            return sessionStorage.getItem(`smart_ad_dismiss_${slot}`) === "1";
        } catch {
            return false;
        }
    });

    useEffect(() => {
        if (dismissed) return;
        api.get(`/ads/active?slot=${slot}`)
            .then((r) => {
                const rows = Array.isArray(r.data) ? r.data : [];
                if (rows.length === 0) {
                    setAd(null);
                    return;
                }
                // Rotate — pick a random one so advertisers share impressions
                setAd(rows[Math.floor(Math.random() * rows.length)]);
            })
            .catch(() => setAd(null));
    }, [slot, dismissed]);

    const dismiss = () => {
        setDismissed(true);
        try { sessionStorage.setItem(`smart_ad_dismiss_${slot}`, "1"); } catch { /* noop */ }
    };

    if (!ad || dismissed) {
        // Default "advertise with us" placeholder — doubles as a lead gen for the ad product
        return (
            <div
                data-testid={`ad-banner-placeholder-${slot}`}
                className={`relative overflow-hidden rounded-xl border border-dashed border-[#FFD700]/30 bg-gradient-to-r from-[#1a1203] to-background px-5 py-3 flex items-center gap-3 ${className}`}
            >
                <div className="w-9 h-9 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/40 grid place-items-center shrink-0">
                    <Megaphone size={14} className="text-[#FFD700]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs text-foreground font-medium truncate">
                        Advertise with SMART
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                        Reach Jamaica's premium on-demand customers · from JMD $20,000 / month
                    </div>
                </div>
                <a
                    href="/advertise"
                    data-testid={`ad-banner-cta-${slot}`}
                    className="shrink-0 px-4 py-2 rounded-full bg-[#FFD700] text-black text-[10px] uppercase tracking-[0.22em] font-semibold hover:bg-amber-300 transition-colors"
                >
                    Buy a slot
                </a>
            </div>
        );
    }

    return (
        <a
            href={ad.link_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`ad-banner-${slot}`}
            className={`relative overflow-hidden rounded-xl border border-[#FFD700]/40 bg-gradient-to-r from-[#2d1a05] via-[#1a1003] to-background hover:border-[#FFD700]/70 transition-all group ${className}`}
        >
            <div className="flex items-center gap-4 p-4 pr-10">
                {ad.image_url && (
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-black/40 border border-white/10 shrink-0">
                        <img src={ad.image_url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-[9px] uppercase tracking-[0.3em] text-amber-300/70 mb-0.5" data-testid={`ad-banner-business-${slot}`}>
                        {ad.business_name} · Sponsored
                    </div>
                    <div className="text-sm sm:text-base text-foreground font-medium" data-testid={`ad-banner-headline-${slot}`}>
                        {ad.headline}
                    </div>
                    {ad.body && (
                        <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                            {ad.body}
                        </div>
                    )}
                </div>
                <div className="text-[10px] uppercase tracking-[0.26em] text-[#FFD700] shrink-0 pr-2">
                    See more →
                </div>
            </div>
            <button
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); dismiss(); }}
                aria-label="Dismiss ad"
                className="absolute top-2 right-2 w-6 h-6 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors z-10"
            >
                <X size={12} />
            </button>
        </a>
    );
}
